import { SELECTOR } from "%/constant";
import { vibrate } from "%/message";
import { Portal } from "./Portal";
import { NavAnchor } from "./NavAnchor";
import { IconHome } from "./Icon/Home";
import { IconMap } from "./Icon/Map";
import { IconInform } from "./Icon/Inform";
import { IconCalendar } from "./Icon/Calendar";
import { IconGallery } from "./Icon/Gallery";
import { IconUser } from "./Icon/User";
import { IconNotify } from "./Icon/Notify";
import { IconFeed } from "./Icon/Feed";
import { ComponentType, useEffect, useMemo, useState } from "react";
import { useLocation, useRouter } from "@tanstack/react-router";
import {
  useNewNotice,
  useRespondGuardianInvite,
} from "~/services/customer/home";
import { useBusinessAuthUserInfo } from "~/services/business/auth";
import { Dialog } from "@/Dialog";
import { FileRouteTypes } from "../routeTree.gen";
import { useNavVisibilityStore } from "&/navVisibility";

interface NavProps {
  type?: "customer" | "business";
}

type NavItem = {
  key: string;
  label: string;
  to: FileRouteTypes["to"];
  Icon: ComponentType<{ active?: boolean }>;
  hasNew?: boolean;
};

export const Nav = ({ type }: NavProps) => {
  const location = useLocation();
  const { history } = useRouter();
  const { data: newNoticeData, refetch: refetchNewNotice } = useNewNotice();
  const { data: businessUserInfo, refetch: refetchBusinessUserInfo } =
    useBusinessAuthUserInfo(false);
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [isReloginModalVisible, setIsReloginModalVisible] = useState(false);
  const respondInviteMutation = useRespondGuardianInvite();
  const visible = useNavVisibilityStore((s) => s.visible);

  useEffect(() => {
    if (type === "customer") {
      const navUrls = [
        "/customer/layout/home",
        "/customer/layout/map",
        "/customer/layout/inform",
        "/customer/layout/calendar",
        "/customer/layout/gallery",
      ];
      if (navUrls.includes(location.pathname)) {
        refetchNewNotice();
      }
    }
  }, [location.pathname, type, refetchNewNotice]);

  useEffect(() => {
    if (type === "customer" && newNoticeData?.hasInvite) {
      setIsInviteModalVisible(true);
    }
  }, [newNoticeData, type]);

  const handleInviteAccept = () => {
    respondInviteMutation.mutate(
      { agree: true },
      {
        onSuccess: () => {
          setIsInviteModalVisible(false);
          setIsReloginModalVisible(true);
        },
        onError: (error) => {
          console.error("보호자 초대 수락 실패:", error);
          globalThis.alert(
            "보호자 초대 수락에 실패했습니다. 다시 시도해주세요.",
          );
        },
      },
    );
  };

  const handleInviteReject = () => {
    respondInviteMutation.mutate(
      { agree: false },
      {
        onSuccess: () => {
          setIsInviteModalVisible(false);
          refetchNewNotice();
        },
        onError: (error) => {
          console.error("보호자 초대 거절 실패:", error);
          globalThis.alert(
            "보호자 초대 거절에 실패했습니다. 다시 시도해주세요.",
          );
        },
      },
    );
  };

  const handleReloginConfirm = () => {
    setIsReloginModalVisible(false);
    history.push("/");
  };

  useEffect(() => {
    if (import.meta.env.VITE_APP_TYPE === "business") {
      const navUrls = [
        "/business/layout/home",
        "/business/layout/to-school",
        "/business/layout/notify",
        "/business/layout/calendar",
        "/business/layout/gallery",
        "/business/layout/user",
        "/business/layout/user-teacher-setting",
      ];
      if (navUrls.includes(location.pathname)) {
        refetchBusinessUserInfo();
      }
    }
  }, [location.pathname, refetchBusinessUserInfo]);

  const fnNavBusiness = (defaultUrl: string) => {
    if (businessUserInfo) {
      if (businessUserInfo.isOwner) {
        return defaultUrl;
      } else if (businessUserInfo.isTeacher) {
        if (businessUserInfo.assignedCompany) {
          if (defaultUrl === "/business/layout/user-owner-setting") {
            return "/business/layout/user-teacher-setting";
          } else {
            return defaultUrl;
          }
        } else {
          if (defaultUrl === "/business/layout/user-owner-setting") {
            return "/business/layout/teacher" as any;
          } else {
            return defaultUrl;
          }
        }
      } else {
        return defaultUrl;
      }
    } else {
      return defaultUrl;
    }
  };

  const customerItems: NavItem[] = useMemo(
    () => [
      { key: "home", label: "홈", to: "/customer/layout/home", Icon: IconHome },
      { key: "map", label: "지도", to: "/customer/layout/map", Icon: IconMap },
      {
        key: "inform",
        label: "알림장",
        to: "/customer/layout/inform",
        Icon: IconInform,
        hasNew: newNoticeData?.readYn === "N",
      },
      {
        key: "feed",
        label: "소식",
        to: "/customer/layout/feed",
        Icon: IconFeed,
      },
      {
        key: "calendar",
        label: "일정",
        to: "/customer/layout/calendar",
        Icon: IconCalendar,
      },
      {
        key: "gallery",
        label: "사진첩",
        to: "/customer/layout/gallery",
        Icon: IconGallery,
      },
    ],
    [newNoticeData?.readYn],
  );

  const businessItems: NavItem[] = useMemo(
    () => [
      {
        key: "home",
        label: "홈",
        to: "/business/layout/home",
        Icon: IconHome,
      },
      {
        key: "notify",
        label: "알림",
        to: "/business/layout/notify",
        Icon: IconNotify,
      },
      {
        key: "calendar",
        label: "일정",
        to: "/business/layout/calendar",
        Icon: IconCalendar,
      },
      {
        key: "gallery",
        label: "사진첩",
        to: "/business/layout/gallery",
        Icon: IconGallery,
      },
      {
        key: "user",
        label: "마이",
        to: fnNavBusiness(
          "/business/layout/user-owner-setting",
        ) as FileRouteTypes["to"],
        Icon: IconUser,
      },
    ],
    [businessUserInfo],
  );

  const items = type === "customer" ? customerItems : businessItems;

  // 보호자/사업자 공통 플로팅 필(pill) nav — 메뉴 항목만 type 에 따라 다름.
  // 데스크톱(md)은 앱 카드가 상하 1.5rem 띄워져 있어 bottom 을 그만큼 보정한다
  const navClass = `nav-float fixed left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[448px] h-16 bg-white rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.1)] flex items-center px-2 z-50 transition-all duration-300 ease-in-out bottom-[calc(var(--nav-bottom,0px)+1rem)] md:bottom-10 ${
    visible ? "opacity-100" : "opacity-0 translate-y-[140%] pointer-events-none"
  }`;

  return (
    <nav className={navClass} onTouchStart={vibrate}>
      {items.map((item) => (
        <NavAnchor
          key={item.key}
          to={item.to}
          label={item.label}
          Icon={item.Icon}
          hasNew={item.hasNew}
        />
      ))}

      {type === "customer" && (
        <>
          <Dialog
            visible={isInviteModalVisible}
            onClose={() => setIsInviteModalVisible(false)}
            persist={false}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">
                  보호자 연동 초대
                </div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  {newNoticeData?.message || ""}
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
                  onClick={handleInviteReject}
                  disabled={respondInviteMutation.isPending}
                >
                  거절
                </button>
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full disabled:bg-gray-300 disabled:cursor-not-allowed"
                  onClick={handleInviteAccept}
                  disabled={respondInviteMutation.isPending}
                >
                  {respondInviteMutation.isPending ? "처리 중..." : "수락"}
                </button>
              </div>
            </div>
          </Dialog>
          <Dialog
            visible={isReloginModalVisible}
            onClose={() => setIsReloginModalVisible(false)}
            persist={false}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col mb-2">
                <div className="text-lg font-semibold text-center">알림</div>
              </div>
              <div className="flex flex-col mb-2">
                <div className="text-sm text-center text-[#666666]">
                  연동을 위해 재로그인 합니다.
                </div>
              </div>
              <div className="flex gap-3 mt-2">
                <button
                  className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
                  onClick={handleReloginConfirm}
                >
                  확인
                </button>
              </div>
            </div>
          </Dialog>
        </>
      )}
    </nav>
  );
};

export const LayoutNav = (props: NavProps) => (
  <Portal id={SELECTOR.NAV}>
    <Nav {...props} />
  </Portal>
);
